/**
 * App.jsx
 * Root application component using Shopify Polaris.
 * Manages top-level period selector, navigation between report tabs,
 * and shared filter state.
 */

import React, { useState, useCallback } from 'react';
import {
  AppProvider,
  Frame,
  Navigation,
  TopBar,
  Page,
  Select,
  InlineStack,
  Button,
  Toast,
  Text,
} from '@shopify/polaris';
import {
  ChartDonutIcon,
  InventoryIcon,
  AlertTriangleIcon,
  GlobeAsiaIcon,
  ProductIcon,
  PackageIcon,
  RefreshIcon,
} from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

import { SummaryPage } from './pages/SummaryPage';
import { SalesPage } from './pages/SalesPage';
import { InventoryPage } from './pages/InventoryPage';
import { DeadStockPage } from './pages/DeadStockPage';
import { GeoPage } from './pages/GeoPage';
import { VendorPage } from './pages/VendorPage';
import { api } from './api/api';

const PERIOD_OPTIONS = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'Last 6 months', value: '6m' },
  { label: 'Last 12 months', value: '12m' },
  { label: 'Last year', value: '1y' },
];

const OVERSTOCK_OPTIONS = [
  { label: '30 days', value: '30' },
  { label: '60 days', value: '60' },
  { label: '90 days', value: '90' },
  { label: '120 days', value: '120' },
  { label: '180 days', value: '180' },
];

const REORDER_OPTIONS = [
  { label: '7 days', value: '7' },
  { label: '14 days', value: '14' },
  { label: '21 days', value: '21' },
  { label: '30 days', value: '30' },
];

const NAV_ITEMS = [
  { id: 'summary', label: 'Summary', icon: ChartDonutIcon },
  { id: 'sales', label: 'Sales Velocity', icon: ProductIcon },
  { id: 'inventory', label: 'Inventory Runway', icon: InventoryIcon },
  { id: 'dead-stock', label: 'Clearance Alerts', icon: AlertTriangleIcon },
  { id: 'geo', label: 'Geo Performance', icon: GlobeAsiaIcon },
  { id: 'vendor', label: 'By Vendor', icon: PackageIcon },
];

export default function App() {
  const [activeView, setActiveView] = useState('summary');
  const [period, setPeriod] = useState('30d');
  const [overstockDays, setOverstockDays] = useState('90');
  const [reorderDays, setReorderDays] = useState('30');
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const showToast = useCallback((message) => {
    setToastMessage(message);
    setToastActive(true);
  }, []);

  const handleClearCache = useCallback(async () => {
    try {
      await api.clearCache();
      showToast('Cache cleared — data will refresh on next load.');
    } catch {
      showToast('Failed to clear cache.');
    }
  }, [showToast]);

  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section
        title="Reports"
        items={NAV_ITEMS.map((item) => ({
          label: item.label,
          icon: item.icon,
          selected: activeView === item.id,
          onClick: () => {
            setActiveView(item.id);
            setMobileNavOpen(false);
          },
        }))}
      />
    </Navigation>
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={() => setMobileNavOpen((v) => !v)}
    />
  );

  // Shared filter bar rendered at top of every page
  const filterBar = (
    <InlineStack gap="300" align="start" blockAlign="center" wrap={false}>
      <Text variant="bodyMd" as="span" tone="subdued">Period:</Text>
      <div style={{ minWidth: 160 }}>
        <Select
          label=""
          options={PERIOD_OPTIONS}
          value={period}
          onChange={setPeriod}
          labelHidden
        />
      </div>
      <Text variant="bodyMd" as="span" tone="subdued">Overstock threshold:</Text>
      <div style={{ minWidth: 120 }}>
        <Select
          label=""
          options={OVERSTOCK_OPTIONS}
          value={overstockDays}
          onChange={setOverstockDays}
          labelHidden
        />
      </div>
      <Text variant="bodyMd" as="span" tone="subdued">Reorder alert:</Text>
      <div style={{ minWidth: 100 }}>
        <Select
          label=""
          options={REORDER_OPTIONS}
          value={reorderDays}
          onChange={setReorderDays}
          labelHidden
        />
      </div>
      <Button
        icon={RefreshIcon}
        onClick={handleClearCache}
        accessibilityLabel="Refresh data"
        size="slim"
        variant="plain"
      >
        Refresh
      </Button>
    </InlineStack>
  );

  const sharedProps = { period, overstockDays: parseInt(overstockDays, 10), reorderDays: parseInt(reorderDays, 10), filterBar };

  const currentPage = {
    summary: <SummaryPage {...sharedProps} onNavigate={setActiveView} />,
    sales: <SalesPage {...sharedProps} />,
    inventory: <InventoryPage {...sharedProps} />,
    'dead-stock': <DeadStockPage {...sharedProps} />,
    geo: <GeoPage {...sharedProps} />,
    vendor: <VendorPage {...sharedProps} />,
  }[activeView];

  return (
    <AppProvider i18n={enTranslations}>
      <Frame
        topBar={topBarMarkup}
        navigation={navigationMarkup}
        showMobileNavigation={mobileNavOpen}
        onNavigationDismiss={() => setMobileNavOpen(false)}
      >
        <Page
          title="Inventory & Sales Planner"
          subtitle="Saree Silhouette — Shopify Plus"
          fullWidth
        >
          {currentPage}
        </Page>

        {toastActive && (
          <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
        )}
      </Frame>
    </AppProvider>
  );
}
